/**
 * SPA Route Parser and URL Builder with OCI Mode Support
 */

import { normalizeEditorHash, EDITOR_LOGIN_HASH } from './editor-gate.js';

export function parseRoute() {
  if (typeof window === 'undefined') {
    return { name: 'home', params: {}, query: new URLSearchParams() };
  }

  const url = new URL(window.location.href);
  const hash = url.hash.replace(/^#/, '').replace(/^\/?/, '');

  // #editor-login (auch #editor-login?x und #editor-login/) ist nur ein Aufruf-Marker, keine Route: Startseite bleibt.
  // Normalisierung identisch zum Editor-Gate (editor-gate.js).
  const isEditorMarker = normalizeEditorHash(url.hash) === EDITOR_LOGIN_HASH;
  const effectivePath = hash && !isEditorMarker ? `/${hash}` : url.pathname;
  const [pathPart = '', queryString = ''] = effectivePath.split('?');
  const parts = pathPart.replace(/^\/?/, '').split('/').filter(Boolean);
  const query = queryString ? new URLSearchParams(queryString) : url.searchParams;

  if (!parts.length) {
    return { name: 'home', params: {}, query };
  }

  const rootSegment = parts[0].toLowerCase();

  if (rootSegment === 'berater') {
    return { name: 'advisor', params: {}, query };
  }

  if (rootSegment === 'oci') {
    return {
      name: 'catalog',
      params: { mode: 'oci' },
      query,
      isOci: true
    };
  }

  if (['hub', 'portal', 'executive', 'betriebsleiter'].includes(rootSegment)) {
    const tab = parts[1] ? decodeURIComponent(parts[1]).toLowerCase() : (query.get('tab') || 'overview').toLowerCase();
    return {
      name: 'hub',
      tab,
      params: {
        tab,
        id: parts[2] || '',
      },
      query,
    };
  }

  if (rootSegment === 'admin') {
    const section = parts[1] || query.get('tab') || 'tenants';
    return {
      name: 'admin',
      section,
      params: {
        section,
        id: parts[2] || '',
      },
      query,
    };
  }

  if (rootSegment === 'catalog') {
    return {
      name: 'catalog',
      categorySlug: parts[1] ? decodeURIComponent(parts[1]) : '',
      subcategorySlug: parts[2] ? decodeURIComponent(parts[2]) : '',
      params: {
        categorySlug: parts[1] ? decodeURIComponent(parts[1]) : '',
        subcategorySlug: parts[2] ? decodeURIComponent(parts[2]) : '',
      },
      query,
    };
  }

  if ((rootSegment === 'products' || rootSegment === 'product') && parts[1]) {
    return {
      name: 'product',
      slug: decodeURIComponent(parts[1]),
      params: {
        slug: decodeURIComponent(parts[1]),
        idOrSlug: decodeURIComponent(parts[1]),
      },
      query,
    };
  }

  // a-047: Stripe-Rückkehr. Muss vor dem allgemeinen /checkout-Mapping stehen.
  if (rootSegment === 'checkout' && parts[1] === 'erfolg') {
    return { name: 'checkout-success', params: {}, query };
  }

  if (rootSegment === 'cart' || rootSegment === 'checkout') {
    return { name: 'cart', params: {}, query };
  }

  if (rootSegment === 'inquiry' && parts[1] === 'success' && parts[2]) {
    return {
      name: 'inquiry-success',
      inquiryId: decodeURIComponent(parts[2]),
      params: { inquiryId: decodeURIComponent(parts[2]) },
      query,
    };
  }

  if (rootSegment === 'success' && parts[1]) {
    return {
      name: 'inquiry-success',
      inquiryId: decodeURIComponent(parts[1]),
      params: { inquiryId: decodeURIComponent(parts[1]) },
      query,
    };
  }

  if (['impressum', 'datenschutz', 'agb', 'versand', 'widerruf', 'kontakt'].includes(rootSegment)) {
    return {
      name: 'legal',
      page: rootSegment,
      params: { page: rootSegment },
      query,
    };
  }

  if (rootSegment === 'legal' && parts[1]) {
    return {
      name: 'legal',
      page: parts[1],
      params: { page: parts[1] },
      query,
    };
  }

  // Unbekannte Pfade: 404-Seite statt Katalog (M3).
  return { name: 'notfound', params: {}, query, path: pathPart };
}

export function buildRouteUrl(route) {
  if (typeof route === 'string') return route.startsWith('/') ? route : `/${route}`;
  if (!route || typeof route !== 'object') return '/';

  switch (route.name) {
    case 'home':
      return '/';
    case 'admin': {
      const section = route.section || route.params?.section || '';
      return section ? `/admin/${section}` : '/admin';
    }
    case 'catalog': {
      const segs = ['/catalog', route.categorySlug || route.params?.categorySlug, route.subcategorySlug || route.params?.subcategorySlug].filter(Boolean);
      const query = new URLSearchParams(route.query || '');
      if (route.aslCategory !== undefined && route.aslCategory !== null && !query.has('aslCategory')) {
        query.set('aslCategory', String(route.aslCategory));
      }
      const queryString = query.toString();
      const qs = queryString ? `?${queryString}` : '';
      return `${segs.join('/')}${qs}`;
    }
    case 'product':
      return `/products/${encodeURIComponent(route.slug || route.params?.slug || route.params?.idOrSlug || '')}`;
    case 'cart':
      return '/cart';
    case 'inquiry-success':
    case 'success':
      return `/inquiry/success/${encodeURIComponent(route.inquiryId || route.params?.inquiryId || '')}`;
    case 'legal':
      return `/${route.page || route.params?.page || 'impressum'}`;
    case 'hub':
    case 'portal':
    case 'executive':
    case 'betriebsleiter': {
      const tab = route.tab || route.params?.tab || '';
      const qs = route.query ? `?${route.query.toString()}` : '';
      return (tab && tab !== 'overview' ? `/hub/${encodeURIComponent(tab)}` : '/hub') + qs;
    }
    default:
      return '/';
  }
}
